import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [viewAsRole, setViewAsRole] = useState(localStorage.getItem('viewAsRole') || '');
  const [viewAsUser, setViewAsUser] = useState(localStorage.getItem('viewAsUser') || '');

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('access_token');
      if (savedToken) {
        try {
          const decoded = jwtDecode(savedToken);
          if (decoded.exp * 1000 > Date.now()) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
            setToken(savedToken);
          } else {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      });

      const { access_token, refresh_token, user: userData } = response.data;
      
      localStorage.setItem('access_token', access_token);
      if (rememberMe) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      // Clear any impersonation state when logging in directly
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonating_as');
      localStorage.removeItem('ghost_protocol_user');
      
      // If user is admin, store their token separately for ghost login
      if (userData?.role === 'super_admin' || userData?.role === 'admin') {
        localStorage.setItem('admin_original_token', access_token);
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      // Handle Pydantic validation errors (detail is array) and standard errors (detail is string)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Login failed';
      if (Array.isArray(detail)) {
        // Pydantic validation error - extract first message
        errorMessage = detail[0]?.msg || 'Invalid input';
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('viewAsRole');
      localStorage.removeItem('viewAsUser');
      localStorage.removeItem('admin_original_token'); // Clear admin token on logout
      localStorage.removeItem('is_impersonating'); // Clear impersonation state
      localStorage.removeItem('impersonating_as');
      localStorage.removeItem('ghost_protocol_user');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setToken(null);
      setViewAsRole('');
      setViewAsUser('');
    }
  };

  const setTestingView = (roleId, userId = '') => {
    setViewAsRole(roleId);
    setViewAsUser(userId);
    
    if (roleId && roleId !== 'super_admin') {
      localStorage.setItem('viewAsRole', roleId);
      if (userId) {
        localStorage.setItem('viewAsUser', userId);
      } else {
        localStorage.removeItem('viewAsUser');
      }
    } else {
      localStorage.removeItem('viewAsRole');
      localStorage.removeItem('viewAsUser');
    }
  };

  const clearTestingView = () => {
    setViewAsRole('');
    setViewAsUser('');
    localStorage.removeItem('viewAsRole');
    localStorage.removeItem('viewAsUser');
  };

  const updateUser = async (updates) => {
    try {
      const response = await axios.patch(`${API}/users/${user.id}`, updates);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      // Handle Pydantic validation errors (detail is array) and standard errors (detail is string)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Update failed';
      if (Array.isArray(detail)) {
        errorMessage = detail[0]?.msg || 'Invalid input';
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'super_admin' || user?.role === 'admin',
    viewAsRole,
    viewAsUser,
    setTestingView,
    clearTestingView
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};